import { DateTime } from 'luxon';
import {
  GetAlarmsForStoreResponse,
  SiteAlarmType,
} from '../../../features/alarms/alarmsAPI';
import { SiteType } from '../../../features/sites/sitesAPI';
import {
  deviceAbsentValidApps,
  nonFilteredAlarmStrings,
} from '../../../components/Alarms/Constants';
import { ConfirmAlarmsResponse } from '../../../features/plivo/plivoAPI.ts';

type Args = {
  alarms: GetAlarmsForStoreResponse | undefined;
  siteData: SiteType | undefined;
  siteId: number;
  siteNameAndLocation: string;
  leoAlertAvailable: boolean;
  isInglesSite: boolean;
  confirmAlarmsData: ConfirmAlarmsResponse | undefined;
  isLoadingConfirmAlarmsData: boolean;
  isFetchingConfirmAlarmsData: boolean;
  isErrorLoadingConfirmAlarms: boolean;
};

export default function categorizeSiteAlarms({
  alarms,
  siteData,
  siteId,
  siteNameAndLocation,
  leoAlertAvailable,
  isInglesSite,
  confirmAlarmsData,
  isLoadingConfirmAlarmsData,
  isFetchingConfirmAlarmsData,
  isErrorLoadingConfirmAlarms,
}: Args) {
  const categorizedAlarms: {
    active: SiteAlarmType[];
    historical: SiteAlarmType[];
    override: SiteAlarmType[];
  } = {
    active: [],
    historical: [],
    override: [],
  };

  if (alarms && alarms[0] && alarms[0].store_num === siteId) {
    for (const alrm of alarms[0].alarms) {
      const alarm = structuredClone(alrm) as SiteAlarmType;
      alarm.siteId = siteId;
      alarm.siteNameAndLocation = siteNameAndLocation;
      alarm.timezone = siteData?.time_zone ?? undefined;
      alarm.filtered = false;

      if (leoAlertAvailable) {
        alarm.leoAlertStatus = 'LOADING';

        if (
          confirmAlarmsData &&
          !isFetchingConfirmAlarmsData &&
          !isLoadingConfirmAlarmsData
        ) {
          const confirmedAlarm = confirmAlarmsData.find(
            (a) =>
              a.alarm_text === alarm.alarm_text &&
              a.alarm_source === alarm.alarm_source &&
              a.controller === alarm.controller_name &&
              a.alarm_date === alarm.alarm_date,
          );
          if (
            confirmedAlarm &&
            (confirmedAlarm.sms_uuids?.length > 0 ||
              confirmedAlarm.voip_uuids?.length > 0)
          ) {
            alarm.leoAlertStatus = 'SENT';
          } else {
            alarm.leoAlertStatus = 'NOT SENT';
          }
        } else if (
          isErrorLoadingConfirmAlarms &&
          !isFetchingConfirmAlarmsData &&
          !isLoadingConfirmAlarmsData
        ) {
          alarm.leoAlertStatus = 'NOT SENT';
        }
      }

      // device absent from network needs to match certain applications

      if (isInglesSite) {
        alarm.filtered = true;
        // check for filtered vals
        if (nonFilteredAlarmStrings.includes(alarm.alarm_text.toLowerCase())) {
          // this is a active alarm to show
          alarm.filtered = false;
        } else if (
          alarm.alarm_text.toLowerCase() === 'device absent from network'
        ) {
          for (const val of deviceAbsentValidApps) {
            if (alarm.alarm_source.toLowerCase().includes(val)) {
              alarm.filtered = false;
              break;
            }
          }
        }
      }

      if (alrm.service_override_date !== null && alrm.alarm_active === 1) {
        // compare date with store timezone if possible``
        // fallback to utc
        // tbh stupid af its the same comparison in the end.
        // just compare in UTC
        if (siteData && siteData.time_zone) {
          const now = DateTime.now().setZone(siteData.time_zone);
          const overrideDate = DateTime.fromISO(
            alrm.service_override_date,
          ).setZone(siteData.time_zone);

          if (now >= overrideDate) {
            categorizedAlarms.active.push(alarm);
          } else {
            categorizedAlarms.override.push(alarm);
          }
        } else {
          const now = DateTime.now().setZone('utc');
          const overrideDate = DateTime.fromISO(
            alrm.service_override_date,
          ).setZone('utc');
          if (now >= overrideDate) {
            categorizedAlarms.active.push(alarm);
          } else {
            categorizedAlarms.override.push(alarm);
          }
        }
      } else if (alrm.alarm_active === 1) {
        categorizedAlarms.active.push(alarm);
      } else {
        categorizedAlarms.historical.push(alarm);
      }
    }
  }
  return categorizedAlarms;
}
